/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, useContext, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import api from '../services/api'
import {
  trackRegistration,
  generateEventId,
  collectCAPIRegistrationMetadata,
  collectCAPIBrowserContext,
  hashEmail,
  hashExternalId,
  identifyHotjarUser,
  identifyGA4User,
} from '../utils/analytics'

export const AuthContext = createContext(null)

// Key for localStorage to track if CompleteRegistration was already sent
const REGISTRATION_TRACKED_KEY = 'capi_registration_tracked'

// Helper to check marketing consent from localStorage
const getMarketingConsent = () => {
  try {
    const stored = localStorage.getItem('cookieConsent')
    if (stored) {
      return JSON.parse(stored).marketing === true
    }
  } catch (e) {
    console.warn('[Auth] Failed to read cookie consent:', e)
  }
  return false
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check for existing session on mount and listen for auth changes
  useEffect(() => {
    // Get initial session
    checkAuth()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, nextSession) => {
        console.log('Auth event:', event)

        // Don't auto-login during PASSWORD_RECOVERY
        // User must complete password reset flow first
        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery detected - waiting for user to set new password')
          setLoading(false)
          return
        }

        if (nextSession?.user) {
          setUser(nextSession.user)
          setSession(nextSession)

          // Identify user in GA4 and Hotjar for cross-device session linking
          const identifyEvents = new Set(['SIGNED_IN', 'INITIAL_SESSION'])
          if (identifyEvents.has(event)) {
            const userId = nextSession.user.id
            const userMetadata = nextSession.user?.user_metadata || {}

            // GA4: Cross-device tracking
            identifyGA4User(userId)

            // Hotjar: Session recordings with user attributes
            identifyHotjarUser(userId, {
              plan: userMetadata.subscription_plan || 'free',
              registered_at: nextSession.user.created_at,
            })
          }

          // Sync CAPI context on login events when marketing consent is given
          const capiSyncEvents = new Set(['SIGNED_IN', 'INITIAL_SESSION'])
          if (capiSyncEvents.has(event) && getMarketingConsent()) {
            try {
              const currentContext = collectCAPIBrowserContext()
              const userMetadata = nextSession.user?.user_metadata || {}
              const updates = {}

              // Only update if new value EXISTS and is DIFFERENT
              if (currentContext.capi_fbp && currentContext.capi_fbp !== userMetadata.capi_fbp) {
                updates.capi_fbp = currentContext.capi_fbp
              }
              if (currentContext.capi_fbc && currentContext.capi_fbc !== userMetadata.capi_fbc) {
                updates.capi_fbc = currentContext.capi_fbc
              }
              if (currentContext.capi_fbclid && currentContext.capi_fbclid !== userMetadata.capi_fbclid) {
                updates.capi_fbclid = currentContext.capi_fbclid
              }

              if (Object.keys(updates).length > 0) {
                console.log('[Auth] Syncing CAPI context:', Object.keys(updates))
                await supabase.auth.updateUser({ data: updates })
              }
            } catch (capiError) {
              console.warn('[Auth] Failed to sync CAPI context:', capiError)
            }
          }

          // Track CompleteRegistration Pixel event ONLY after email confirmation (DOI)
          // Note: CAPI tracking is now handled by backend webhook (supabase_user_webhook)
          // This Pixel event is kept for deduplication with CAPI
          const registrationEvents = new Set(['SIGNED_IN', 'INITIAL_SESSION', 'USER_UPDATED'])
          if (registrationEvents.has(event) && nextSession.user.email_confirmed_at) {
            const userId = nextSession.user.id
            const trackedKey = `${REGISTRATION_TRACKED_KEY}_${userId}`

            // Only track once per user (check localStorage)
            if (!localStorage.getItem(trackedKey)) {
              try {
                const userEmail = nextSession.user.email
                const userMetadata = nextSession.user.user_metadata || {}

                // Use same event_id format as backend webhook for deduplication
                // Format: {userId}_CompleteRegistration_{capi_registered_at}
                const registeredAt = userMetadata.capi_registered_at || ''
                const eventId = `${userId}_CompleteRegistration_${registeredAt}`

                // Hash email and external_id for Advanced Matching
                const [hashedEmail, hashedExternalId] = await Promise.all([
                  hashEmail(userEmail),
                  hashExternalId(userId)
                ])

                // Track with Facebook Pixel (with Advanced Matching)
                // CAPI is handled by backend webhook - this is for deduplication
                trackRegistration({
                  eventId,
                  hashedEmail,
                  hashedExternalId,
                })

                // Mark as tracked so we don't send again
                localStorage.setItem(trackedKey, 'true')
                console.log('[Analytics] CompleteRegistration Pixel tracked (CAPI via backend webhook)')
              } catch (analyticsError) {
                // Non-critical: don't block auth flow
                console.warn('[Analytics] CompleteRegistration Pixel tracking failed:', analyticsError)
              }
            }
          }
        } else {
          setUser(null)
          setSession(null)
        }

        setLoading(false)
      }
    )

    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe()
    }
  }, [])

  const checkAuth = async () => {
    try {
      setLoading(true)
      const { data: { session: currentSession }, error } = await supabase.auth.getSession()

      if (error) throw error

      if (currentSession?.user) {
        setUser(currentSession.user)
        setSession(currentSession)
      } else {
        setUser(null)
        setSession(null)
      }
    } catch (err) {
      console.error('Auth check failed:', err)
      setUser(null)
      setSession(null)
    } finally {
      setLoading(false)
    }
  }

  const logout = useCallback(async () => {
    try {
      const { error: signOutError } = await supabase.auth.signOut()
      if (signOutError) throw signOutError
    } catch (err) {
      console.error('Logout failed:', err)
      throw err
    } finally {
      setUser(null)
      setSession(null)
    }
  }, [])

  const refreshSession = useCallback(async () => {
    try {
      const { data, error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) throw refreshError

      const refreshedSession = data?.session ?? null

      if (refreshedSession?.user) {
        setUser(refreshedSession.user)
      } else {
        setUser(null)
      }

      setSession(refreshedSession)
      return refreshedSession
    } catch (err) {
      console.error('Session refresh failed:', err)
      await logout()
      return null
    }
  }, [logout])

  const login = async (email, password) => {
    try {
      setError(null)
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      setUser(data.user)
      if (data.session) {
        setSession(data.session)
      }

      // Check if CAPI browser context is missing and fill it
      // This catches users who registered without fbp/fbc cookies
      try {
        const userMetadata = data.user?.user_metadata || {}
        const currentContext = collectCAPIBrowserContext()
        const updates = {}

        // Only add values that are missing in user_metadata
        if (!userMetadata.capi_fbp && currentContext.capi_fbp) {
          updates.capi_fbp = currentContext.capi_fbp
        }
        if (!userMetadata.capi_fbc && currentContext.capi_fbc) {
          updates.capi_fbc = currentContext.capi_fbc
        }
        if (!userMetadata.capi_fbclid && currentContext.capi_fbclid) {
          updates.capi_fbclid = currentContext.capi_fbclid
        }

        // Only update if we have new data to add
        if (Object.keys(updates).length > 0) {
          console.log('[Auth] Updating missing CAPI context:', Object.keys(updates))
          await supabase.auth.updateUser({ data: updates })
        }
      } catch (capiError) {
        // Non-critical: don't block login flow
        console.warn('[Auth] Failed to update CAPI context:', capiError)
      }

      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = err.message || 'Login failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const loginWithFacebook = async () => {
    try {
      setError(null)
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          scopes: 'email'
        }
      })

      if (error) throw error
      return { success: true, data }
    } catch (err) {
      const errorMessage = err.message || 'Facebook login failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const register = async (email, password, metadata = {}) => {
    try {
      setError(null)

      // Collect CAPI metadata for backend-side conversion tracking
      // This data is stored in Supabase user_metadata and used by the
      // backend webhook for all CAPI events (Registration, Purchase, etc.)
      let capiMetadata = {}
      try {
        capiMetadata = collectCAPIRegistrationMetadata()
      } catch (capiError) {
        // Non-critical: don't block registration if CAPI metadata collection fails
        console.warn('[Auth] Failed to collect CAPI metadata:', capiError)
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...metadata, // Additional user metadata (display_name, email_preferences, etc.)
            ...capiMetadata, // CAPI browser context for backend tracking
          },
        },
      })

      if (error) throw error

      // Supabase returns user without session when email confirmation is required
      // But if user already exists (unconfirmed), it returns user with empty identities
      if (data?.user?.identities?.length === 0) {
        // User already exists but hasn't confirmed email
        throw new Error('email_already_registered')
      }

      // Note: User will need to confirm email before they can log in
      // depending on Supabase email confirmation settings
      if (data.session) {
        setSession(data.session)
      }

      return {
        success: true,
        user: data.user,
        needsEmailConfirmation: !data.session // No session means email confirmation needed
      }
    } catch (err) {
      // Safely get error message
      const rawMessage = err?.message || err?.error_description || String(err) || 'Registration failed'
      let errorMessage = rawMessage

      // Check for rate limit error (Supabase returns this in various languages)
      const messageLower = rawMessage.toLowerCase()
      const isRateLimitError = messageLower.includes('rate') ||
        messageLower.includes('limit') ||
        messageLower.includes('velocità') || // Italian
        messageLower.includes('limite') || // Italian/Spanish/Portuguese
        messageLower.includes('geschwindigkeit') || // German
        err?.status === 429

      if (isRateLimitError) {
        errorMessage = 'email_rate_limit' // Use translation key
      } else if (rawMessage === 'email_already_registered') {
        errorMessage = 'email_already_registered' // Use translation key
      }

      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const resetPassword = async (email) => {
    try {
      setError(null)
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error

      return { success: true }
    } catch (err) {
      const errorMessage = err.message || 'Password reset failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      setError(null)
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error

      return { success: true }
    } catch (err) {
      const errorMessage = err.message || 'Password update failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateEmail = async (newEmail) => {
    try {
      setError(null)
      const oldEmail = user.email // Store current email before update

      // Step 1: Clean up old email from Brevo lists
      try {
        await api.post('/preferences/email-subscriptions', {
          email: oldEmail,
          productUpdates: false,
          marketingEmails: false
        })
      } catch (brevoError) {
        // Log but don't block email update - Brevo cleanup is best-effort
        console.warn('Brevo cleanup failed (non-blocking):', brevoError)
      }

      // Step 2: Update email in Supabase
      const { data, error } = await supabase.auth.updateUser({
        email: newEmail,
      })

      if (error) throw error

      setUser(data.user)
      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = err.message || 'Email update failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateUser = async (updates) => {
    try {
      setError(null)
      const { data, error } = await supabase.auth.updateUser({
        data: updates,
      })

      if (error) throw error

      setUser(data.user)
      return { success: true, user: data.user }
    } catch (err) {
      const errorMessage = err.message || 'User update failed'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const getAccessToken = async () => {
    try {
      const { data: { session: currentSession }, error } = await supabase.auth.getSession()
      if (error) throw error
      return currentSession?.access_token
    } catch (err) {
      console.error('Failed to get access token:', err)
      return null
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !session?.expires_at) {
      return
    }

    const intervalId = window.setInterval(() => {
      const expiresAtMs = session.expires_at * 1000
      const timeUntilExpiry = expiresAtMs - Date.now()

      if (timeUntilExpiry <= 5 * 60 * 1000) {
        refreshSession()
      }
    }, 60 * 1000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [refreshSession, session])

  const value = {
    user,
    session,
    loading,
    error,
    login,
    loginWithFacebook,
    register,
    logout,
    checkAuth,
    refreshSession,
    updateUser,
    updateEmail,
    resetPassword,
    updatePassword,
    getAccessToken,
    isAuthenticated: !!user,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook for using auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
